import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAdminProfile, getAllProductsUnpaginated, getAllOrders, getAllCustomers, getAllNotifications, markNotificationAsRead, IMAGE_BASE_URL } from '../services/api';
import { usePermission } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';
import './AdminLayout.css';

var resolveImageUrl = function (path) {
  if (!path || typeof path !== 'string' || !path.trim()) return '';
  var p = path.replace(/\\/g, '/').trim();
  if (p.startsWith('blob:') || p.startsWith('data:')) return p;
  if (p.startsWith('http://') || p.startsWith('https://')) return p;
  var base = (IMAGE_BASE_URL || '').replace(/\/$/, '');
  return base ? base + '/' + p.replace(/^\/+/, '') : '/' + p.replace(/^\/+/, '');
};

var resolveProductImage = function (obj) {
  if (!obj || typeof obj !== 'object') return '';
  if (Array.isArray(obj.images) && obj.images.length > 0) {
    var first = obj.images[0];
    if (typeof first === 'string' && first.trim()) return resolveImageUrl(first.trim());
    if (first && typeof first === 'object') {
      var inner = first.url || first.src || first.path || first.image;
      if (typeof inner === 'string' && inner.trim()) return resolveImageUrl(inner.trim());
    }
  }
  var strFields = ['image', 'imageUrl', 'image_url', 'imageURL', 'img', 'photo', 'thumbnail', 'picture', 'cover', 'avatar', 'banner', 'url'];
  for (var i = 0; i < strFields.length; i++) {
    var value = obj[strFields[i]];
    if (typeof value === 'string' && value.trim()) return resolveImageUrl(value.trim());
    if (value && typeof value === 'object') {
      var inner = value.url || value.src || value.path || value.image;
      if (typeof inner === 'string' && inner.trim()) return resolveImageUrl(inner.trim());
    }
  }
  var arrFields = ['photos', 'pictures', 'thumbnails'];
  for (var j = 0; j < arrFields.length; j++) {
    var arr = obj[arrFields[j]];
    if (Array.isArray(arr) && arr.length > 0) {
      var firstArr = arr[0];
      if (typeof firstArr === 'string' && firstArr.trim()) return resolveImageUrl(firstArr.trim());
      if (firstArr && typeof firstArr === 'object') {
        var inner = firstArr.url || firstArr.src || firstArr.path || firstArr.image;
        if (typeof inner === 'string' && inner.trim()) return resolveImageUrl(inner.trim());
      }
    }
  }
  return '';
};

var STORAGE_KEY = 'adminProfileData';
var READ_ORDER_NOTIFS_KEY = 'adminReadOrderNotifications';

var readCache = function () {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
  catch (e) { return {}; }
};

var writeCache = function (patch) {
  try {
    var prev = readCache();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.assign({}, prev, patch)));
  } catch (e) {}
};

var EarthwiLogo = function (props) {
  return React.createElement('img', {
    src: '/ishmai_LOGO.png',
    alt: 'ISHMAI Jewellery logo',
    className: 'earthwi-logo' + (props.collapsed ? ' is-collapsed' : '')
  });
};

var NAV_ITEMS = [
  {
    id: 'dashboard',
    path: '/admin/dashboard',
    label: 'Dashboard',
    section: 'Main',
    // chart-pie / dashboard icon
    icon: 'M11 2v20c-5.07-.5-9-4.79-9-10s3.93-9.5 9-10zm2.03 0v8.99H22c-.47-4.74-4.24-8.52-8.97-8.99zm0 11.01V22c4.74-.47 8.5-4.25 8.97-8.99h-8.97z'
  },
  {
    id: 'categories',
    path: '/admin/category',
    label: 'Categories',
    section: 'Products',
    // folder-special / categories grid icon
    icon: 'M20 6h-8l-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V6h5.17l2 2H20v10z'
  },
  {
    id: 'subcategories',
    path: '/admin/subcategory',
    label: 'Subcategories',
    section: 'Products',
    // folder with tree / subfolder icon
    icon: 'M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9h-4v4h-2v-4H9V9h4V5h2v4h4v2z'
  },
  {
    id: 'products',
    path: '/admin/products',
    label: 'Products',
    section: 'Products',
    // shopping bag icon
    icon: 'M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-8 4c0 .55-.45 1-1 1s-1-.45-1-1V8h2v2zm2-6c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2zm4 6c0 .55-.45 1-1 1s-1-.45-1-1V8h2v2z'
  },
  {
    id: 'about',
    path: '/admin/about',
    label: 'About',
    section: 'Products',
    // info page icon
    icon: 'M11 17h2v-6h-2v6zm1-14C6.48 3 2 7.48 2 13s4.48 10 10 10 10-4.48 10-10S17.52 3 12 3zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-12h2V7h-2v2z'
  },
  {
    id: 'banners',
    path: '/admin/banners',
    label: 'Banners',
    section: 'Products',
    icon: 'M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z'
  },
  {
    id: 'scrollbar',
    path: '/admin/scrollbar',
    label: 'Scrollbar Banners',
    section: 'Products',
    icon: 'M4 6h16v3H4V6zm0 5h16v3H4v-3zm0 5h16v3H4v-3z'
  },
  {
    id: 'coupons',
    path: '/admin/coupons',
    label: 'Coupons',
    section: 'Products',
    icon: 'M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v1.5a2.5 2.5 0 0 1 0 5V18a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5.5a2.5 2.5 0 0 1 0-5zM8 8h8v2H8V8zm0 4h5v2H8v-2zm8.5 3.5A1.5 1.5 0 1 1 15 14a1.5 1.5 0 0 1 1.5 1.5z'
  },
  {
    id: 'orders',
    path: '/admin/orders',
    label: 'Orders',
    section: 'Orders & Customers',
    // receipt / order list icon
    icon: 'M18 17H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2zM3 22l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20z'
  },
  {
    id: 'customers',
    path: '/admin/customers',
    label: 'Customers',
    section: 'Orders & Customers',
    // group / customers icon
    icon: 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z'
  },
  {
    id: 'contact',
    path: '/admin/contact',
    label: 'Contact',
    section: 'Orders & Customers',
    // envelope / contact icon
    icon: 'M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z'
  },
  {
    id: 'payments',
    path: '/admin/payments',
    label: 'Payments',
    section: 'Payments & Reports',
    // wallet icon
    icon: 'M21 7H3c-.55 0-1 .45-1 1v13c0 .55.45 1 1 1h18c.55 0 1-.45 1-1V8c0-.55-.45-1-1-1zm-1 13H4v-6h16v6zm0-9H4V9h16v2zm-2-7H4c-.55 0-1 .45-1 1H2c0-1.1.9-2 2-2h14l2 2h-1zM16 15h2v2h-2z'
  },
  {
    id: 'supercoins',
    path: '/admin/supercoins',
    label: 'ISHMAI Coins',
    section: 'Payments & Reports',
    // coins icon
    icon: 'M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8zm1-13h-2v2H9v2h2v2H9v2h2v2h2v-2h2v-2h-2v-2h2V9h-2z'
  },
  {
    id: 'giftpacking',
    path: '/admin/giftpacking',
    label: 'Gift Packing',
    section: 'Payments & Reports',
    icon: 'M20 12v10H4V12m18-5H2v5h20V7zM12 22V7m0 0H7.5a2.5 2.5 0 1 1 0-5C11 2 12 7 12 7zm0 0h4.5a2.5 2.5 0 1 0 0-5C13 2 12 7 12 7z'
  },
  {
    id: 'reports',
    path: '/admin/reports',
    label: 'Reports',
    section: 'Payments & Reports',
    icon: 'M4 19h16v2H4v-2zm2-2h3V9H6v8zm5 0h3V3h-3v14zm5 0h3v-6h-3v6z'
  },
  {
    id: 'settings',
    path: '/admin/settings',
    label: 'Settings',
    section: 'Administration',
    // gear / settings icon
    icon: 'M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z'
  },
  {
    id: 'usersroles',
    path: '/admin/roles',
    label: 'Roles & Permissions',
    section: 'Administration',
    icon: 'M16 11c1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm8 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zM8 13c-2.67 0-6 1.34-6 4v2h5v-2.5c0-1.08.45-2.08 1.22-2.92A7.59 7.59 0 0 0 8 13z'
  },
  {
    id: 'employees',
    path: '/admin/employees',
    label: 'Employees',
    section: 'Administration',
    icon: 'M16 11c1.66 0 3-1.34 3-3s1.34-3-3-3-3 1.34-3 3 1.34 3 3 3zM8 11c1.66 0 3-1.34 3-3S9.66 5 8 5 5 6.34 5 8s1.34 3 3 3zm8 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zM8 13c-2.67 0-6 1.34-6 4v2h5v-2.5c0-1.08.45-2.08 1.22-2.92A7.59 7.59 0 0 0 8 13z'
  },
];

var NAV_GROUPS = ['Main', 'Products', 'Orders & Customers', 'Payments & Reports', 'Administration'];
var NAV_PERMISSION_MODULE = {
  dashboard: 'dashboard',
  categories: 'categories',
  subcategories: 'subcategories',
  products: 'products',
  about: 'about',
  banners: 'banners',
  scrollbar: 'scrollbar',
  coupons: 'coupons',
  orders: 'orders',
  customers: 'customers',
  contact: 'contact',
  payments: 'payments',
  supercoins: 'supercoins',
  giftpacking: 'giftpacking',
  reports: 'reports',
  settings: 'settings',
  usersroles: 'roles',
  employees: 'employees',
};

var extractArray = function (res) {
  try {
    var d = res && res.data;
    if (!d) return [];
    if (Array.isArray(d)) return d;
    if (Array.isArray(d.data)) return d.data;
    if (d.data && Array.isArray(d.data.products)) return d.data.products;
    if (d.data && Array.isArray(d.data.orders)) return d.data.orders;
    if (d.data && Array.isArray(d.data.customers)) return d.data.customers;
    if (Array.isArray(d.products)) return d.products;
    if (Array.isArray(d.orders)) return d.orders;
    if (Array.isArray(d.customers)) return d.customers;
    if (Array.isArray(d.result)) return d.result;
    return [];
  } catch (e) { return []; }
};

var resolveObjectId = function (value) {
  if (!value) return '';
  if (typeof value === 'object') return value._id || value.id || '';
  return String(value);
};

var getCustomerName = function (order) {
  if (!order) return '';
  if (order.user && typeof order.user === 'object') return order.user.name || order.user.fullName || order.user.email || '';
  if (order.userId && typeof order.userId === 'object') return order.userId.name || order.userId.fullName || order.userId.email || '';
  if (order.customer && typeof order.customer === 'object') return order.customer.name || order.customer.fullName || order.customer.email || '';
  if (order.customerId && typeof order.customerId === 'object') return order.customerId.name || order.customerId.fullName || order.customerId.email || '';
  return order.customerName || order.customerEmail || order.email || '';
};

var getCustomerEmail = function (order) {
  if (!order) return '';
  if (order.user && typeof order.user === 'object') return order.user.email || '';
  if (order.userId && typeof order.userId === 'object') return order.userId.email || '';
  if (order.customer && typeof order.customer === 'object') return order.customer.email || '';
  if (order.customerId && typeof order.customerId === 'object') return order.customerId.email || '';
  return order.customerEmail || order.email || '';
};

var searchableValue = function (value) {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) return value.map(searchableValue).join(' ');
  if (typeof value === 'object') return Object.keys(value).concat(Object.values(value)).map(searchableValue).join(' ');
  return String(value);
};

var TYPE_META = {
  product:  { label: 'Product',  color: '#3E7C8C', bg: 'rgba(62,124,140,0.10)',  path: '/admin/products'  },
  order:    { label: 'Order',    color: '#F28C28', bg: 'rgba(242,140,40,0.10)',  path: '/admin/orders'    },
  customer: { label: 'Customer', color: '#059669', bg: 'rgba(5,150,105,0.10)',   path: '/admin/customers' },
};

var fmtINR = function (n) { return '\u20B9' + (Number(n) || 0).toLocaleString('en-IN'); };

var timeAgo = function (dateStr) {
  if (!dateStr) return '';
  var diff  = Date.now() - new Date(dateStr).getTime();
  var mins  = Math.floor(diff / 60000);
  var hours = Math.floor(diff / 3600000);
  var days  = Math.floor(diff / 86400000);
  if (mins  < 1)  return 'Just now';
  if (mins  < 60) return mins  + 'm ago';
  if (hours < 24) return hours + 'h ago';
  return days + 'd ago';
};

var getStatusEmoji = function (status) {
  var s = (status || '').toLowerCase();
  if (s === 'delivered')  return '\u2705';
  if (s === 'cancelled')  return '\u274C';
  if (s === 'shipped')    return '🚚';
  if (s === 'processing') return '⚙️';
  return '🛒';
};

var buildBellNotifs = function (notifications) {
  return (Array.isArray(notifications) ? notifications : [])
    .filter(function (n) { return n && n._id; })
    .map(function (n) {
      var status    = n.status    || 'Pending';
      var createdAt = n.createdAt || new Date().toISOString();
      var amount    = (n.amount !== undefined && n.amount !== null) ? fmtINR(n.amount) : '';
      var sub       = n.message || (amount ? amount + (status ? ' \u00B7 ' + status : '') : status);
      var isStockAlert = status === 'Low Stock' || status === 'Out of Stock';
      var productId = resolveObjectId(n.productId);
      return {
        id: n._id,
        title: n.title || 'Notification',
        sub: sub,
        time: createdAt,
        ts: new Date(createdAt).getTime(),
        emoji: isStockAlert ? (status === 'Out of Stock' ? '\u26D4' : '\u26A0\uFE0F') : getStatusEmoji(status),
        isRead: n.isRead || false,
        navPath: isStockAlert && productId ? '/admin/products?productId=' + encodeURIComponent(productId) : undefined,
      };
    })
    .sort(function (a, b) { return b.ts - a.ts; });
};

var buildOrderBellNotifs = function (orders) {
  var readIds = readOrderNotificationIds();
  return (Array.isArray(orders) ? orders : [])
    .filter(function (o) { return o && o._id; })
    .map(function (order) {
      var createdAt = order.createdAt || order.updatedAt || new Date().toISOString();
      var status = order.orderStatus || order.status || 'Pending';
      var amount = order.totalAmount || order.total || 0;
      var customerName = getCustomerName(order) || 'Guest';
      var customerEmail = getCustomerEmail(order);
      var customerLabel = customerName + (customerEmail ? ' (' + customerEmail + ')' : '');
      return {
        id: 'order-' + order._id,
        orderId: order._id,
        title: 'New order received',
        sub: customerLabel + ' · ' + fmtINR(amount) + ' · ' + status,
        time: createdAt,
        ts: new Date(createdAt).getTime(),
        emoji: '🛒',
        isRead: readIds.indexOf(order._id) !== -1,
        type: 'order',
        navPath: '/admin/orders?orderId=' + encodeURIComponent(order._id)
      };
    })
    .sort(function (a, b) { return b.ts - a.ts; })
    .slice(0, 25);
};

var mergeBellNotifications = function (notifications, orderNotifications) {
  var merged = [];
  var seen = new Set();

  var pushItem = function (item) {
    if (!item) return;
    var key = item.orderId ? 'order:' + item.orderId : 'notif:' + (item.id || item._id);
    if (!key || seen.has(key)) return;
    seen.add(key);
    merged.push(item);
  };

  (Array.isArray(notifications) ? notifications : []).forEach(pushItem);
  (Array.isArray(orderNotifications) ? orderNotifications : []).forEach(pushItem);

  return merged.sort(function (a, b) {
    var aTs = a && a.ts ? a.ts : 0;
    var bTs = b && b.ts ? b.ts : 0;
    return bTs - aTs;
  });
};

var readOrderNotificationIds = function () {
  try {
    var raw = localStorage.getItem(READ_ORDER_NOTIFS_KEY);
    var parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(function (id) { return typeof id === 'string' && id; }) : [];
  } catch (e) {
    return [];
  }
};

var writeReadOrderNotificationIds = function (ids) {
  try {
    var unique = Array.from(new Set((Array.isArray(ids) ? ids : []).filter(function (id) { return typeof id === 'string' && id; })));
    localStorage.setItem(READ_ORDER_NOTIFS_KEY, JSON.stringify(unique));
  } catch (e) {}
};

var markOrderNotificationsRead = function (ids) {
  writeReadOrderNotificationIds(readOrderNotificationIds().concat(Array.isArray(ids) ? ids : []));
};

/* ══════════════════════════════════════
   GLOBAL SEARCH
   ══════════════════════════════════════ */
var GlobalSearch = function () {
  var navigate    = useNavigate();
  var _sq = useState('');    var query     = _sq[0];  var setQuery     = _sq[1];
  var _sr = useState([]);    var results   = _sr[0];  var setResults   = _sr[1];
  var _so = useState(false); var open      = _so[0];  var setOpen      = _so[1];
  var _sl = useState(false); var loading   = _sl[0];  var setLoading   = _sl[1];
  var _sf = useState(0);     var focused   = _sf[0];  var setFocused   = _sf[1];
  var _sc = useState(null);  var dataCache = _sc[0];  var setDataCache = _sc[1];
  var inputRef    = useRef(null);
  var dropdownRef = useRef(null);
  var debounceRef = useRef(null);

  var loadData = useCallback(function () {
    if (dataCache) return Promise.resolve(dataCache);
    return Promise.allSettled([getAllProductsUnpaginated(), getAllOrders(), getAllCustomers()])
      .then(function (res) {
        var cache = {
          products:  res[0].status === 'fulfilled' ? extractArray(res[0].value) : [],
          orders:    res[1].status === 'fulfilled' ? extractArray(res[1].value) : [],
          customers: res[2].status === 'fulfilled' ? extractArray(res[2].value) : [],
        };
        setDataCache(cache);
        return cache;
      })
      .catch(function () { return { products: [], orders: [], customers: [] }; });
  }, [dataCache]);

  var runSearch = useCallback(function (q) {
    var trimmed = q.trim().toLowerCase();
    if (!trimmed) { setResults([]); setOpen(false); return; }
    setLoading(true);
    loadData().then(function (data) {
      var matched = [];
      data.products
        .filter(function (p) { return searchableValue(p).toLowerCase().includes(trimmed); })
        .slice(0, 5)
        .forEach(function (p) {
          matched.push({
            type: 'product',
            id: p._id || p.id,
            name: p.name || 'Unnamed Product',
            sub: p.price ? '\u20B9' + p.price + (p.status ? ' \u00B7 ' + p.status : '') : '',
            emoji: '\uD83D\uDCE6',
            image: resolveProductImage(p),
            navPath: '/admin/products?productId=' + encodeURIComponent(p._id || p.id)
          });
        });
      data.orders
        .filter(function (o) { return searchableValue(o).toLowerCase().includes(trimmed); })
        .slice(0, 5)
        .forEach(function (o) { matched.push({ type: 'order', id: o._id || o.id, name: 'Order #' + String(o._id || o.id || '').slice(-8).toUpperCase(), sub: (getCustomerName(o) || 'Guest') + ' \u00B7 \u20B9' + Number(o.totalAmount || o.total || o.amount || 0).toLocaleString('en-IN') + ' \u00B7 ' + (o.orderStatus || o.status || 'Pending'), emoji: '\uD83D\uDED2', navPath: '/admin/orders?orderId=' + encodeURIComponent(o._id || o.id) }); });
      data.customers
        .filter(function (c) { return searchableValue(c).toLowerCase().includes(trimmed); })
        .slice(0, 5)
        .forEach(function (c) { matched.push({ type: 'customer', id: c._id || c.id, name: c.name || c.fullName || c.customerName || 'Unknown Customer', sub: c.email || c.phone || '', emoji: '\uD83D\uDC64', navPath: '/admin/customers?customerId=' + encodeURIComponent(c._id || c.id) }); });
      setResults(matched); setOpen(true); setFocused(0);
    }).finally(function () { setLoading(false); });
  }, [loadData]);

  var handleChange = function (e) {
    var val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    if (!val.trim()) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(function () { runSearch(val); }, 280);
  };

  var handleSelect = function (item) { navigate(item.navPath); setQuery(''); setResults([]); setOpen(false); };

  var handleKeyDown = function (e) {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocused(function (f) { return Math.min(f + 1, results.length - 1); }); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setFocused(function (f) { return Math.max(f - 1, 0); }); }
    if (e.key === 'Enter' && results[focused]) handleSelect(results[focused]);
    if (e.key === 'Escape') { setOpen(false); if (inputRef.current) inputRef.current.blur(); }
  };

  useEffect(function () {
    var h = function (e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) && inputRef.current && !inputRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return function () { document.removeEventListener('mousedown', h); };
  }, []);

  var groups = ['product', 'order', 'customer'].reduce(function (acc, type) {
    var items = results.filter(function (r) { return r.type === type; });
    if (items.length) acc.push({ type: type, items: items });
    return acc;
  }, []);
  var globalIdx = 0;

  return React.createElement('div', { className: 'gs-wrap' },
    React.createElement('div', { className: 'gs-input-row' },
      React.createElement('svg', { className: 'gs-icon', viewBox: '0 0 24 24' },
        React.createElement('path', { d: 'M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z' })
      ),
      React.createElement('input', {
        ref: inputRef, className: 'gs-input', type: 'text',
        placeholder: 'Search products, orders, customers\u2026',
        value: query, onChange: handleChange,
        onFocus: function () { loadData(); },
        onKeyDown: handleKeyDown,
        autoComplete: 'off'
      }),
      loading && React.createElement('div', { className: 'gs-spinner' }),
      (query && !loading) && React.createElement('button', {
        className: 'gs-clear',
        onClick: function () { setQuery(''); setResults([]); setOpen(false); if (inputRef.current) inputRef.current.focus(); }
      }, '\u2715')
    ),
    open && React.createElement('div', { className: 'gs-dropdown', ref: dropdownRef },
      results.length === 0
        ? React.createElement('div', { className: 'gs-empty' }, 'No results for "' + query + '"')
        : React.createElement(React.Fragment, null,
            groups.map(function (group, gi) {
              var meta = TYPE_META[group.type];
              return React.createElement('div', { key: group.type },
                gi > 0 && React.createElement('div', { className: 'gs-divider' }),
                React.createElement('div', { className: 'gs-section-hd' }, meta.label + 's'),
                group.items.map(function (item) {
                  var idx = globalIdx++;
                  return React.createElement('button', {
                    key: item.id,
                    className: 'gs-result' + (focused === idx ? ' focused' : ''),
                    onMouseEnter: function () { setFocused(idx); },
                    onClick: function () { handleSelect(item); }
                  },
                    React.createElement('div', {
                      className: 'gs-result-icon',
                      style: {
                        position: 'relative',
                        background: item.image ? 'rgba(255,255,255,0.05)' : meta.bg,
                        overflow: 'hidden',
                        borderRadius: 12,
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }
                    },
                      item.image
                        ? React.createElement('img', {
                            src: item.image,
                            alt: item.name || 'item',
                            onError: function (e) { e.target.style.display = 'none'; },
                            style: { width: 32, height: 32, objectFit: 'cover', borderRadius: 10, display: 'block' }
                          })
                        : React.createElement('span', {
                            style: {
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 16,
                              color: '#FFFFFF',
                            }
                          }, item.emoji)
                    ),
                    React.createElement('div', { className: 'gs-result-body' },
                      React.createElement('div', { className: 'gs-result-name' }, item.name),
                      item.sub && React.createElement('div', { className: 'gs-result-sub' }, item.sub)
                    ),
                    React.createElement('span', { className: 'gs-badge', style: { color: meta.color, background: meta.bg } }, meta.label)
                  );
                })
              );
            }),
            React.createElement('div', { className: 'gs-footer' }, results.length + ' result' + (results.length !== 1 ? 's' : '') + ' \u00B7 \u2191\u2193 navigate \u00B7 Enter to open \u00B7 Esc to close')
          )
    )
  );
};

/* ══════════════════════════════════════
   NOTIFICATION BELL
   ══════════════════════════════════════ */
var NotificationBell = function () {
  var navigate = useNavigate();
  var _nn = useState([]);    var notifs      = _nn[0]; var setNotifs      = _nn[1];
  var _uc = useState(0);     var unreadCount = _uc[0]; var setUnreadCount = _uc[1];
  var _op = useState(false); var open        = _op[0]; var setOpen        = _op[1];
  var _ms = useState(false); var markSuccess = _ms[0]; var setMarkSuccess = _ms[1];
  var pollRef  = useRef(null);
  var bellRef  = useRef(null);
  var panelRef = useRef(null);

  var fetchNotifications = useCallback(function () {
    Promise.allSettled([getAllNotifications(), getAllOrders()])
      .then(function (res) {
        var notificationRes = res[0];
        var ordersRes = res[1];
        var notificationRaw = (notificationRes && notificationRes.status === 'fulfilled' && notificationRes.value && notificationRes.value.data && (Array.isArray(notificationRes.value.data) ? notificationRes.value.data : notificationRes.value.data.data || notificationRes.value.data.notifications || [])) || [];
        var orderList = ordersRes && ordersRes.status === 'fulfilled' ? extractArray(ordersRes.value) : [];
        var list = mergeBellNotifications(buildBellNotifs(notificationRaw), buildOrderBellNotifs(orderList));
        setNotifs(list);
        setUnreadCount(list.filter(function (n) { return !n.isRead; }).length);
      })
      .catch(function () { setNotifs([]); setUnreadCount(0); });
  }, []);

  useEffect(function () {
    fetchNotifications();
    pollRef.current = setInterval(fetchNotifications, 15000);
    return function () { clearInterval(pollRef.current); };
  }, [fetchNotifications]);

  useEffect(function () {
    var handler = function () { fetchNotifications(); };
    window.addEventListener('adminNotifUpdated', handler);
    return function () { window.removeEventListener('adminNotifUpdated', handler); };
  }, [fetchNotifications]);

  useEffect(function () { if (open) fetchNotifications(); }, [open, fetchNotifications]);

  useEffect(function () {
    var handler = function (e) {
      if (!open) return;
      if (panelRef.current && !panelRef.current.contains(e.target) && bellRef.current && !bellRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return function () { document.removeEventListener('mousedown', handler); };
  }, [open]);

  var markAllRead = function () {
    var unread = (Array.isArray(notifs) ? notifs : []).filter(function (n) { return n && !n.isRead; });
    if (unread.length === 0) return;
    setNotifs(function (prev) { return (Array.isArray(prev) ? prev : []).map(function (n) { return Object.assign({}, n, { isRead: true }); }); });
    setUnreadCount(0);
    markOrderNotificationsRead(unread.filter(function (n) { return n && n.orderId; }).map(function (n) { return n.orderId; }));
    Promise.all(unread.filter(function (n) { return n && n._id && !n.orderId; }).map(function (n) { return markNotificationAsRead(n._id).catch(function () {}); }))
      .then(function () {
        window.dispatchEvent(new Event('adminNotifUpdated'));
        setMarkSuccess(true);
        setTimeout(function () { setMarkSuccess(false); }, 1800);
        fetchNotifications();
      });
  };

  var handleNotificationClick = function (id) {
    var selected = (Array.isArray(notifs) ? notifs : []).find(function (n) { return (n._id || n.id) === id; });
    setNotifs(function (prev) {
      return (Array.isArray(prev) ? prev : []).map(function (n) {
        if (!n) return n;
        return (n._id || n.id) !== id ? n : Object.assign({}, n, { isRead: true });
      });
    });
    setUnreadCount(function (prev) { return Math.max(0, prev - 1); });
    if (selected && selected.orderId) {
      markOrderNotificationsRead([selected.orderId]);
      window.dispatchEvent(new Event('adminNotifUpdated'));
      fetchNotifications();
    }
    if (selected && selected.navPath) {
      setOpen(false);
      if (!selected.orderId) {
        markNotificationAsRead(id)
          .then(function () { window.dispatchEvent(new Event('adminNotifUpdated')); })
          .catch(function () {});
      } else {
        window.dispatchEvent(new Event('adminNotifUpdated'));
      }
      navigate(selected.navPath);
      return;
    }
    if (selected && selected.orderId) {
      setOpen(false);
      return;
    }
    markNotificationAsRead(id)
      .then(function () { window.dispatchEvent(new Event('adminNotifUpdated')); fetchNotifications(); })
      .catch(function () { fetchNotifications(); });
  };

  var preview = (Array.isArray(notifs) ? notifs : []).filter(function (n) { return !n.isRead; }).slice(0, 6);
  var hasNotifications = Array.isArray(notifs) && notifs.length > 0;

  return React.createElement('div', { className: 'nb-wrap' },
    React.createElement('button', {
      ref: bellRef, className: 'nb-btn', onClick: function () { setOpen(function (p) { return !p; }); },
      title: unreadCount > 0 ? unreadCount + ' unread notifications' : 'Notifications'
    },
      React.createElement('svg', { viewBox: '0 0 24 24' },
        React.createElement('path', { d: 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9' }),
        React.createElement('path', { d: 'M13.73 21a2 2 0 0 1-3.46 0' })
      ),
      unreadCount > 0 && React.createElement('span', { className: 'nb-badge' }, unreadCount > 99 ? '99+' : unreadCount)
    ),
    open && React.createElement('div', { className: 'nb-panel', ref: panelRef },
      React.createElement('div', { className: 'nb-hdr' },
        React.createElement('div', { className: 'nb-hdr-left' },
          React.createElement('span', { className: 'nb-title' }, 'Notifications'),
          unreadCount > 0 && React.createElement('span', { className: 'nb-count-pill' }, unreadCount)
        ),
        React.createElement('div', { className: 'nb-hdr-actions' },
          React.createElement('button', { type: 'button', className: 'nb-mark-all', disabled: unreadCount === 0, onClick: markAllRead }, 'Mark all read')
        )
      ),
      markSuccess && React.createElement('div', { className: 'nb-all-read-msg' }, '\u2713 All notifications marked as read'),
      React.createElement('div', { className: 'nb-list' },
        preview.length === 0
          ? React.createElement('div', { className: 'nb-empty' },
              React.createElement('div', { className: 'nb-empty-icon' }, '\uD83D\uDCED'),
              React.createElement('div', { className: 'nb-empty-txt' }, 'No unread notifications')
            )
          : preview.map(function (n) {
              return React.createElement('button', {
                key: n.id, type: 'button',
                className: 'nb-item' + (!n.isRead ? ' is-unread' : ''),
                onClick: function () { return handleNotificationClick(n.id); }
              },
                React.createElement('div', { className: 'nb-emoji' }, n.emoji),
                React.createElement('div', { className: 'nb-body' },
                  React.createElement('div', { className: 'nb-item-title' }, n.title),
                  n.sub && React.createElement('div', { className: 'nb-item-sub' }, n.sub),
                  React.createElement('div', { className: 'nb-item-time' }, timeAgo(n.time))
                )
              );
            })
      ),
      React.createElement('div', { className: 'nb-footer' },
        React.createElement('button', { className: 'nb-view-all', disabled: !hasNotifications, onClick: function () { setOpen(false); navigate('/admin/notifications'); } }, 'See all notifications')
      )
    )
  );
};

/* ══════════════════════════════════════
   ADMIN LAYOUT
   ══════════════════════════════════════ */
var AdminLayout = function (props) {
  var navigate  = useNavigate();
  var location  = useLocation();
  var _permission = usePermission();
  var hasPermission = _permission.hasPermission;
  var role = _permission.role;
  var auth = useAuth();

  var _sc = useState(function () { return typeof window !== 'undefined' && window.innerWidth <= 900; }); var collapsed   = _sc[0]; var setCollapsed   = _sc[1];
  var _sp = useState(false); var profileOpen = _sp[0]; var setProfileOpen = _sp[1];
  var _lc = useState(false); var logoutConfirmOpen = _lc[0]; var setLogoutConfirmOpen = _lc[1];

  var _sn = useState(function () { var d = readCache(); return d.fullName || 'Admin'; });
  var adminName = _sn[0]; var setAdminName = _sn[1];

  var _si = useState(function () {
    var d = readCache();
    if (d.profileImageDataUrl && d.profileImageDataUrl.startsWith('data:')) return d.profileImageDataUrl;
    return resolveImageUrl(d.profileImage || '');
  });
  var avatarImg = _si[0]; var setAvatarImg = _si[1];
  var _ie = useState(false); var imgFailed = _ie[0]; var setImgFailed = _ie[1];

  var dropdownRef = useRef(null);
  var sidebarW    = collapsed ? '72px' : '256px';

  useEffect(function () {
    if (avatarImg) setImgFailed(false);
  }, [avatarImg]);

  useEffect(function () {
    getAdminProfile()
      .then(function (res) {
        if (!res || !res.data || !res.data.data) return;
        var d      = res.data.data;
        var img    = resolveImageUrl(d.profileImage || '');
        var cached = readCache();
        setAdminName(d.fullName || 'Admin');
        setImgFailed(false);
        if (img) { setAvatarImg(img); }
        else if (cached.profileImageDataUrl && cached.profileImageDataUrl.startsWith('data:')) { setAvatarImg(cached.profileImageDataUrl); }
        else if (cached.profileImage) { setAvatarImg(resolveImageUrl(cached.profileImage)); }
        writeCache({ fullName: d.fullName || 'Admin', email: d.email || '', mobile: d.mobile || '', profileImage: d.profileImage || cached.profileImage || '' });
      })
      .catch(function () {});
  }, []);

  useEffect(function () {
    var handler = function (e) {
      if (!e.detail) return;
      if (e.detail.profileImage !== undefined) {
        setAvatarImg(e.detail.profileImage || ''); setImgFailed(false);
        var patch = {};
        if (typeof e.detail.profileImage === 'string') {
          if (e.detail.profileImage.startsWith('data:')) patch.profileImageDataUrl = e.detail.profileImage;
          else patch.profileImage = e.detail.profileImage;
        }
        if (Object.keys(patch).length) writeCache(patch);
      }
      if (e.detail.fullName) { setAdminName(e.detail.fullName); writeCache({ fullName: e.detail.fullName }); }
    };
    window.addEventListener('adminProfileUpdated', handler);
    return function () { window.removeEventListener('adminProfileUpdated', handler); };
  }, []);

  useEffect(function () {
    var handler = function (e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setProfileOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return function () { document.removeEventListener('mousedown', handler); };
  }, []);

  var logout = function () {
    localStorage.removeItem(STORAGE_KEY);
    auth.logout();
    navigate('/admin/login');
  };

  var hideLayout = props.hideLayoutOnModal || false;
  var avatarLetter = adminName ? adminName.charAt(0).toUpperCase() : 'A';

  return React.createElement('div', null,

    /* ── Sidebar ── */
    React.createElement('aside', { className: 'sb', 'data-collapsed': collapsed, style: { display: hideLayout ? 'none' : 'flex' } },

      React.createElement('div', { className: 'logo-area' },
        React.createElement(EarthwiLogo, { collapsed: collapsed })
      ),

      React.createElement('div', { className: 'nav-container' },
        NAV_GROUPS.map(function (group) {
          var items = NAV_ITEMS.filter(function (item) {
            if (item.section !== group) return false;
            var module = NAV_PERMISSION_MODULE[item.id];
            return !module || hasPermission(module, 'view');
          });
          if (!items.length) return null;
          return React.createElement('div', { className: 'nav-group', key: group },
            React.createElement('div', { className: 'nl' }, group),
            items.map(function (item) {
              var isActive = location.pathname === item.path;
              return React.createElement('button', {
                key: item.id,
                className: 'ni ' + (isActive ? 'active' : ''),
                onClick: function () { navigate(item.path); if (window.innerWidth <= 900) setCollapsed(true); },
                title: collapsed ? item.label : undefined
              },
                React.createElement('svg', { viewBox: '0 0 24 24', dangerouslySetInnerHTML: { __html: '<path d="' + item.icon + '"/>' } }),
                React.createElement('span', null, item.label)
              );
            })
          );
        })
      ),

    ),

    /* ── Header ── */
    React.createElement('header', { className: 'hdr', style: { left: sidebarW, display: hideLayout ? 'none' : 'flex' } },
      React.createElement('button', { className: 'tog', onClick: function () { setCollapsed(function (p) { return !p; }); } },
        React.createElement('svg', { viewBox: '0 0 24 24', style: { width: '20px', height: '20px', fill: 'currentColor' } },
          React.createElement('path', { d: 'M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z' })
        )
      ),
      React.createElement(GlobalSearch, null),
      React.createElement('div', { className: 'hdr-right' },
        React.createElement(NotificationBell, null),
        React.createElement('div', { ref: dropdownRef, className: 'hdr-avatar-wrap' },
          React.createElement('div', { className: 'hdr-profile-text' },
            React.createElement('span', { className: 'hdr-profile-name' }, adminName),
            React.createElement('span', { className: 'hdr-profile-role' }, (role && role.name) || 'Admin')
          ),
          React.createElement('button', {
            className: 'admin-avatar-btn',
            onClick: function () { setProfileOpen(function (p) { return !p; }); },
            title: adminName
          },
            avatarImg && !imgFailed
              ? React.createElement('img', { src: avatarImg, alt: adminName, onError: function () { setImgFailed(true); } })
              : React.createElement('span', null, avatarLetter)
          ),
          profileOpen && React.createElement('div', { className: 'profile-dropdown' },
            React.createElement('div', { className: 'dropdown-profile-header' },
              React.createElement('div', { className: 'dropdown-avatar-large' },
                avatarImg && !imgFailed
                  ? React.createElement('img', { src: avatarImg, alt: adminName, onError: function () { setImgFailed(true); } })
                  : React.createElement('span', null, avatarLetter)
              ),
              React.createElement('div', { className: 'dropdown-admin-info' },
                React.createElement('div', { className: 'dropdown-admin-name' }, adminName),
                React.createElement('div', { className: 'dropdown-admin-role' }, (role && role.name) || 'Administrator')
              )
            ),
            React.createElement('button', {
              className: 'profile-dropdown-item',
              onClick: function () { setProfileOpen(false); navigate('/admin/profile'); }
            },
              React.createElement('svg', { className: 'dropdown-icon', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' },
                React.createElement('path', { d: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2' }),
                React.createElement('circle', { cx: '12', cy: '7', r: '4' })
              ),
              'Profile'
            ),
            React.createElement('button', {
                className: 'profile-dropdown-item logout',
                onClick: function () { setProfileOpen(false); setLogoutConfirmOpen(true); }
              },
              React.createElement('svg', { className: 'dropdown-icon', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' },
                React.createElement('path', { d: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' }),
                React.createElement('polyline', { points: '16 17 21 12 16 7' }),
                React.createElement('line', { x1: '21', y1: '12', x2: '9', y2: '12' })
              ),
              'Logout'
            )
          )
        )
      )
    ),

    /* ── Main Content ── */
    React.createElement('main', { className: 'main-content', style: { marginLeft: hideLayout ? '0' : sidebarW } },
      props.children
    ),

    /* ── Footer ── */
    React.createElement('footer', { className: 'layout-footer', style: { marginLeft: hideLayout ? '0' : sidebarW, display: hideLayout ? 'none' : 'flex' } },
      React.createElement('span', { className: 'footer-text' }, '\u00A9 2026 ISHMAI Jewellery. All rights reserved.'),
      React.createElement('div', { className: 'footer-links' },
        ['Privacy Policy', 'Terms & Conditions'].map(function (link) {
          return React.createElement('span', { key: link, className: 'footer-link' }, link);
        })
      )
    ),

    /* ── Logout Confirmation Modal ── */
    logoutConfirmOpen && React.createElement('div', {
      className: 'logout-confirm-overlay',
      onClick: function () { setLogoutConfirmOpen(false); }
    },
      React.createElement('div', {
        className: 'logout-confirm-card',
        onClick: function (e) { e.stopPropagation(); }
      },
        React.createElement('div', { className: 'logout-confirm-icon', 'aria-hidden': 'true' },
          React.createElement('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round' },
            React.createElement('path', { d: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' }),
            React.createElement('polyline', { points: '16 17 21 12 16 7' }),
            React.createElement('line', { x1: '21', y1: '12', x2: '9', y2: '12' })
          )
        ),
        React.createElement('div', { className: 'logout-confirm-title' }, 'Confirm Logout'),
        React.createElement('div', { className: 'logout-confirm-message' },
          'Are you sure you want to logout?'
        ),
        React.createElement('div', { className: 'logout-confirm-actions' },
          React.createElement('button', {
            onClick: function () { setLogoutConfirmOpen(false); },
            className: 'logout-confirm-cancel'
          }, 'Cancel'),
          React.createElement('button', {
            onClick: function () { setLogoutConfirmOpen(false); logout(); },
            className: 'logout-confirm-submit'
          }, 'Yes, Logout')
        )
      )
    )
  );
};

export default AdminLayout;






